import ICatalog_Item from "@models/ICatalog_Item";
import ICatalog_Item_Option from "@models/ICatalog_Item_Option";
import { useEffect, useMemo, useState } from "react";
import { OrderItemOption } from "./OrderItemOption/OrderItemOption";
import INewOrder_Item_Option from "@models/INewOrder_Item_Option";

/**
 * Returns a copy of the catalog item's options sorted by their per-item display_order.
 * The API does not order the Options include, so consumers sort here.
 */
export const sortCatalogItemOptions = (options?: ICatalog_Item_Option[]): ICatalog_Item_Option[] => {
    return [...(options || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
};

/**
 * Returns a copy of selected option values ordered to match the catalog item's option display_order,
 * so the values are saved and displayed in the same order the options are presented.
 * Values whose option is not on the catalog item keep their relative order at the end.
 */
export const sortValuesByCatalogItemOptionOrder = <T extends { item_option_id?: number }>(
    values: T[],
    options?: ICatalog_Item_Option[]
): T[] => {
    const rankByOptionId = new Map(sortCatalogItemOptions(options).map((option, index) => [option.item_option_id, index]));
    const rankOf = (value: T) => rankByOptionId.get(value.item_option_id as number) ?? Number.MAX_SAFE_INTEGER;

    return [...values].sort((a, b) => rankOf(a) - rankOf(b));
};

type Props = {
    catalogItem: ICatalog_Item;
    selectedItemOptionsFn: (options: any) => void;
    flexDirection?: "flex-row" | "flex-column";
    /**
     * Existing values to pre-fill (edit flow). Only values whose item_option_id is one of the
     * catalog item's Options are kept; they are emitted through selectedItemOptionsFn on mount so
     * the parent sees them without the user touching anything.
     */
    initialValues?: Partial<INewOrder_Item_Option>[];
};

export const OrderItemOptions = (props: Props) => {
    const [orderItemOptions, setOrderItemOptions] = useState<Partial<INewOrder_Item_Option>[]>(() => {
        const optionIds = new Set((props.catalogItem.Options || []).map((option) => option.item_option_id));

        return sortValuesByCatalogItemOptionOrder(
            (props.initialValues || [])
                .filter((value) => value.item_option_id !== undefined && optionIds.has(value.item_option_id))
                .map((value) => ({ item_option_id: value.item_option_id, value: value.value })),
            props.catalogItem.Options
        );
    });

    useEffect(() => {
        // console.log("OrderItemOptions", orderItemOptions);

        props.selectedItemOptionsFn(orderItemOptions);
    }, [orderItemOptions]);

    const itemOptionsCount = useMemo(() => props.catalogItem.Options?.length || 0, [props.catalogItem.Options]);

    // Honor the display_order set in the catalog item sidebar
    const orderedOptions = useMemo(() => sortCatalogItemOptions(props.catalogItem.Options), [props.catalogItem.Options]);

    const selectedItemOptionsFn = (option: Partial<INewOrder_Item_Option>) => {
        const updatedOrderItemOptions = [...orderItemOptions];

        const existingOption = updatedOrderItemOptions.find((o) => o.item_option_id === option.item_option_id);

        if (existingOption) {
            existingOption.value = option.value;
        } else {
            updatedOrderItemOptions.push(option);
        }

        // Emit in display order so the saved rows (and every read-back of them) follow the sidebar ordering
        setOrderItemOptions(sortValuesByCatalogItemOptionOrder(updatedOrderItemOptions, props.catalogItem.Options));
    };

    return (
        <div className={"OrderItemOptions"}>
            {itemOptionsCount > 100 ? (
                <div>{/* Show Modal */}</div>
            ) : (
                <div className={`d-flex gap-2 ${props.flexDirection || "flex-row "}`}>
                    {orderedOptions.map((option) => {
                        const initial = orderItemOptions.find((o) => o.item_option_id === option.item_option_id);

                        return (
                            <OrderItemOption
                                key={option.Item_Option?.id}
                                option={option}
                                selectedItemOptionFn={selectedItemOptionsFn}
                                initialValue={initial?.value !== undefined && initial?.value !== null ? String(initial.value) : undefined}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};
